import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { jwtDecode } from "jwt-decode";
import { ResponseData } from "../APIs/types";
import { ActivateAccountRequest } from "../types/auth/activate-account.types";
import { UserRoles, UserStatus } from "../types/user.types";
import { AuthAPI } from "../APIs/auth.api";
import { decodeToken } from "../utils/token.utils";

interface User {
  id: number;
  roleId: UserRoles;
  status: UserStatus;
  teamId?: number;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
}

interface AuthContextType {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  login: (username: string, password: string) => Promise<ResponseData<any>>;
  register: (username: string, password: string) => Promise<ResponseData<any>>;
  activateAccount: (data: ActivateAccountRequest) => Promise<ResponseData<any>>;
  logout: () => void;
  validateToken: () => boolean;
  updateTokens: (accessToken: string, refreshToken: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    const storedAccessToken = localStorage.getItem("accessToken");
    const storedRefreshToken = localStorage.getItem("refreshToken");

    if (!storedAccessToken || !storedRefreshToken) {
      return { accessToken: null, refreshToken: null, user: null };
    }

    try {
      const decoded = decodeToken(storedAccessToken);
      if (!decoded) {
        return { accessToken: null, refreshToken: null, user: null };
      }
      return {
        accessToken: storedAccessToken,
        refreshToken: storedRefreshToken,
        user: {
          roleId: decoded.roleId,
          status: decoded.userStatus,
          id: decoded.userId,
          teamId: decoded.teamId ?? undefined,
        },
      };
    } catch {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      return { accessToken: null, refreshToken: null, user: null };
    }
  });

  const clearAuth = useCallback(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setAuthState({ accessToken: null, refreshToken: null, user: null });
    window.dispatchEvent(new Event("tokenUpdate"));
  }, []);

  const updateTokens = useCallback(
    (accessToken: string, refreshToken: string) => {
      try {
        const decoded: any = jwtDecode(accessToken);
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        setAuthState({
          accessToken,
          refreshToken,
          user: {
            roleId: decoded.roleId,
            status: decoded.userStatus,
            id: decoded.userId,
            teamId: decoded.teamId,
          },
        });

        window.dispatchEvent(new Event("tokenUpdate"));
      } catch (error) {
        clearAuth();
      }
    },
    [clearAuth]
  );

  const validateToken = useCallback((): boolean => {
    if (!authState.accessToken) return false;

    try {
      const decodedToken: any = jwtDecode(authState.accessToken);

      return decodedToken.userStatus !== UserStatus.INACTIVE;
    } catch (error) {
      return false;
    }
  }, [authState.accessToken]);

  const handleLogin = async (
    username: string,
    password: string
  ): Promise<ResponseData<any>> => {
    const response = await AuthAPI.login(username, password);
    if (
      response.statusCode === 200 &&
      response.data.accessToken &&
      response.data.refreshToken
    ) {
      updateTokens(response.data.accessToken, response.data.refreshToken);
    }
    return response;
  };

  const handleRegister = async (
    username: string,
    password: string
  ): Promise<ResponseData<any>> => {
    const response = await AuthAPI.register(username, password);
    if (
      response.statusCode === 201 &&
      response.data.accessToken &&
      response.data.refreshToken
    ) {
      updateTokens(response.data.accessToken, response.data.refreshToken);
    }
    return response;
  };

  const handleActivateAccount = async (
    data: ActivateAccountRequest
  ): Promise<ResponseData<any>> => {
    const response = await AuthAPI.activateAccount(data);
    if (
      response.statusCode === 201 &&
      response.data.accessToken &&
      response.data.refreshToken
    ) {
      updateTokens(response.data.accessToken, response.data.refreshToken);
    }
    return response;
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken: authState.accessToken,
        refreshToken: authState.refreshToken,
        user: authState.user,
        login: handleLogin,
        register: handleRegister,
        logout: clearAuth,
        activateAccount: handleActivateAccount,
        validateToken,
        updateTokens,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
